import React, { useCallback, useEffect, useRef, useState } from "react";

import { getDashboard } from "../services/api";

import {
    Warehouse,
    Package,
    Truck,
    Boxes,
    RefreshCw,
    ArrowDownToLine
} from "lucide-react";

import "./Dashboard.css";

const Dashboard = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [refreshing, setRefreshing] = useState(false);

    const mounted = useRef(false);
    const requestId = useRef(0);
    const requestInProgress = useRef(false);
    const hasLoadedData = useRef(false);

    // -----------------------------------------------------
    // LOAD / REFRESH DASHBOARD
    // -----------------------------------------------------

    const loadDashboard = useCallback(async (force = false) => {
        if (!mounted.current) return;

        // Avoid unnecessary duplicate requests.
        // A forced refresh invalidates the older request.
        if (requestInProgress.current && !force) return;

        const currentRequestId = ++requestId.current;

        requestInProgress.current = true;

        if (hasLoadedData.current) {
            setRefreshing(true);
        } else {
            setLoading(true);
            setError("");
        }

        try {
            const response = await getDashboard();

            // Ignore results from older requests or an unmounted page.
            if (
                !mounted.current ||
                currentRequestId !== requestId.current
            ) {
                return;
            }

            if (response?.data?.success && response?.data?.data) {
                const dashboardData = response.data.data;

                setData(dashboardData);
                hasLoadedData.current = true;
                setError("");
            } else {
                const message =
                    response?.data?.message ||
                    "Unable to load dashboard. Please retry.";

                if (!hasLoadedData.current) {
                    setError(message);
                } else {
                    console.error("Dashboard refresh failed:", message);
                }
            }
        } catch (err) {
            if (
                !mounted.current ||
                currentRequestId !== requestId.current
            ) {
                return;
            }

            console.error("Dashboard Error:", err);

            if (!hasLoadedData.current) {
                let message = err?.response?.data?.message;

                if (err?.code === "ECONNABORTED") {
                    message =
                        "The dashboard request timed out. Check your connection and retry.";
                } else if (err?.response) {
                    message =
                        message ||
                        "The server could not load the dashboard. Please retry.";
                } else {
                    message =
                        message ||
                        "Unable to connect to the server. Check your connection and retry.";
                }

                setError(message);
            }
        } finally {
            // Only the newest request may change request status.
            if (
                mounted.current &&
                currentRequestId === requestId.current
            ) {
                requestInProgress.current = false;
                setLoading(false);
                setRefreshing(false);
            }
        }
    }, []);

    // -----------------------------------------------------
    // INITIAL LOAD, TAB RETURN, NETWORK RECOVERY, AUTO REFRESH
    // -----------------------------------------------------

    useEffect(() => {
        mounted.current = true;

        const refreshWhenVisible = () => {
            if (
                document.visibilityState === "visible" &&
                mounted.current
            ) {
                loadDashboard(true);
            }
        };

        const refreshOnPageShow = () => {
            if (mounted.current) {
                loadDashboard(true);
            }
        };

        const refreshOnOnline = () => {
            if (mounted.current) {
                loadDashboard(true);
            }
        };

        // Initial dashboard load.
        loadDashboard(true);

        // Refresh when a mobile browser returns to this tab.
        document.addEventListener(
            "visibilitychange",
            refreshWhenVisible
        );

        // Handle browser back-forward cache and page restoration.
        window.addEventListener("pageshow", refreshOnPageShow);

        // Retry when internet connectivity returns.
        window.addEventListener("online", refreshOnOnline);

        // Also refresh when the browser window regains focus.
        window.addEventListener("focus", refreshOnPageShow);

        // Refresh every 30 seconds while the tab is visible.
        const interval = window.setInterval(() => {
            if (document.visibilityState === "visible") {
                loadDashboard();
            }
        }, 30000);

        return () => {
            mounted.current = false;

            // Invalidate any pending request when leaving this page.
            requestId.current += 1;
            requestInProgress.current = false;

            window.clearInterval(interval);

            document.removeEventListener(
                "visibilitychange",
                refreshWhenVisible
            );

            window.removeEventListener("pageshow", refreshOnPageShow);
            window.removeEventListener("online", refreshOnOnline);
            window.removeEventListener("focus", refreshOnPageShow);
        };
    }, [loadDashboard]);

    // -----------------------------------------------------
    // NUMBER FORMAT
    // -----------------------------------------------------

    const number = (value) => {
        const parsed = Number(value ?? 0);

        return (Number.isFinite(parsed) ? parsed : 0).toLocaleString(
            "en-IN",
            { maximumFractionDigits: 2 }
        );
    };

    // -----------------------------------------------------
    // SAFE PROGRESS BAR VALUE
    // -----------------------------------------------------

    const percentage = (value) => {
        const parsed = Number(value ?? 0);

        if (!Number.isFinite(parsed)) return 0;

        return Math.max(0, Math.min(parsed, 100));
    };

    // -----------------------------------------------------
    // CHAMBER DISPLAY NAME
    // Database values remain unchanged.
    // C-01 -> A, C-02 -> B, C-03 -> C, C-04 -> D
    // -----------------------------------------------------

    const getChamberDisplayName = (chamberNo) => {
        const chamberMap = {
            "C-01": "A",
            "C-02": "B",
            "C-03": "C",
            "C-04": "D"
        };

        const normalized = String(chamberNo ?? "")
            .trim()
            .toUpperCase();

        return chamberMap[normalized] || chamberNo || "-";
    };

    // -----------------------------------------------------
    // INITIAL LOADING STATE
    // Show this only before the first successful load.
    // During refresh, keep existing dashboard content visible.
    // -----------------------------------------------------

    if (loading && !data) {
        return (
            <div className="page-loading">
                <RefreshCw size={20} />
                <span>Loading dashboard...</span>
            </div>
        );
    }

    // -----------------------------------------------------
    // ERROR STATE
    // -----------------------------------------------------

    if (error && !data) {
        return (
            <div className="page-error">
                <h3>Dashboard unavailable</h3>

                <p>{error}</p>

                <button
                    type="button"
                    onClick={() => loadDashboard(true)}
                    disabled={refreshing}
                >
                    <RefreshCw size={16} />
                    Retry
                </button>
            </div>
        );
    }

    // Prevent a blank page if no data is available yet.
    if (!data) {
        return (
            <div className="page-error">
                <h3>Dashboard unavailable</h3>

                <p>
                    The dashboard data is not available yet. Please try again.
                </p>

                <button
                    type="button"
                    onClick={() => loadDashboard(true)}
                >
                    <RefreshCw size={16} />
                    Retry
                </button>
            </div>
        );
    }

    // -----------------------------------------------------
    // DASHBOARD DATA
    // -----------------------------------------------------

    const summary = data.summary || {};
    const chambers = Array.isArray(data.chambers) ? data.chambers : [];
    const activity = Array.isArray(data.recentActivity)
        ? data.recentActivity
        : [];

    // -----------------------------------------------------
    // RENDER DASHBOARD
    // -----------------------------------------------------

    return (
        <div className="dashboard-page">
            {/* PAGE HEADER */}
            <div className="page-header">
                <div>
                    <h2>Dashboard</h2>

                    <p>
                        Bhimashankar Agro Cold Storage overview
                    </p>
                </div>

                <button
                    type="button"
                    className="refresh-button"
                    onClick={() => loadDashboard(true)}
                    disabled={refreshing}
                    aria-label="Refresh dashboard"
                >
                    <RefreshCw
                        size={16}
                        className={refreshing ? "dashboard-refreshing" : ""}
                    />
                    {refreshing ? "Refreshing..." : "Refresh"}
                </button>
            </div>

            {/* SUMMARY CARDS */}
            <div className="stats-grid">
                <div className="stat-card">
                    <div className="stat-icon blue">
                        <Warehouse />
                    </div>

                    <span>Total Capacity</span>

                    <strong>
                        {number(summary.totalCapacityTons)} Tons
                    </strong>
                </div>

                <div className="stat-card">
                    <div className="stat-icon orange">
                        <Package />
                    </div>

                    <span>Occupied</span>

                    <strong>
                        {number(summary.occupiedTons)} Tons
                    </strong>
                </div>

                <div className="stat-card">
                    <div className="stat-icon green">
                        <Boxes />
                    </div>

                    <span>Available</span>

                    <strong>
                        {number(summary.availableTons)} Tons
                    </strong>
                </div>

                <div className="stat-card">
                    <div className="stat-icon purple">
                        <Warehouse />
                    </div>

                    <span>Occupancy</span>

                    <strong>
                        {number(summary.occupancyPercentage)}%
                    </strong>
                </div>

                <div className="stat-card">
                    <div className="stat-icon teal">
                        <Package />
                    </div>

                    <span>Total Bags</span>

                    <strong>{number(summary.totalBags)}</strong>
                </div>

                <div className="stat-card">
                    <div className="stat-icon blue">
                        <ArrowDownToLine />
                    </div>

                    <span>Today's GRNs</span>

                    <strong>{number(summary.todayGRNs)}</strong>
                </div>

                <div className="stat-card">
                    <div className="stat-icon red">
                        <Truck />
                    </div>

                    <span>Today's Deliveries</span>

                    <strong>{number(summary.todayDeliveries)}</strong>
                </div>
            </div>

            {/* OVERALL STORAGE */}
            <div className="content-card">
                <div className="card-header">
                    <div>
                        <h3>Overall Storage</h3>

                        <p>Current storage utilization</p>
                    </div>

                    <strong>
                        {number(summary.occupancyPercentage)}%
                    </strong>
                </div>

                <div
                    className="progress-bar"
                    role="progressbar"
                    aria-label="Overall storage utilization"
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuenow={percentage(summary.occupancyPercentage)}
                >
                    <div
                        style={{
                            width: `${percentage(summary.occupancyPercentage)}%`
                        }}
                    />
                </div>

                <div className="progress-info">
                    <span>
                        Occupied:{" "}
                        <b>{number(summary.occupiedTons)} Tons</b>
                    </span>

                    <span>
                        Available:{" "}
                        <b>{number(summary.availableTons)} Tons</b>
                    </span>
                </div>
            </div>

            {/* CHAMBER STATUS */}
            <div className="content-card">
                <div className="card-header">
                    <div>
                        <h3>Chamber Status</h3>

                        <p>Live chamber occupancy</p>
                    </div>
                </div>

                <div className="chambers-grid">
                    {chambers.map((chamber, index) => {
                        const displayName = getChamberDisplayName(
                            chamber.chamberNo
                        );

                        return (
                            <div
                                className="chamber-mini-card"
                                key={
                                    chamber.id ??
                                    chamber.chamberNo ??
                                    index
                                }
                            >
                                <div className="chamber-mini-header">
                                    <div>
                                        <h4>{displayName}</h4>

                                        <span>
                                            Chamber {displayName}
                                        </span>
                                    </div>

                                    <small>{chamber.status || "-"}</small>
                                </div>

                                <h3>
                                    {number(chamber.occupiedTons)}{" "}
                                    <small>
                                        / {number(chamber.capacityTons)} Tons
                                    </small>
                                </h3>

                                <div
                                    className="progress-bar small"
                                    role="progressbar"
                                    aria-label={`Chamber ${displayName} occupancy`}
                                    aria-valuemin={0}
                                    aria-valuemax={100}
                                    aria-valuenow={percentage(
                                        chamber.occupancyPercentage
                                    )}
                                >
                                    <div
                                        style={{
                                            width: `${percentage(
                                                chamber.occupancyPercentage
                                            )}%`
                                        }}
                                    />
                                </div>

                                <div className="chamber-info">
                                    <span>Available</span>

                                    <b>
                                        {number(chamber.availableTons)} Tons
                                    </b>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* RECENT ACTIVITY */}
            <div className="content-card">
                <div className="card-header">
                    <div>
                        <h3>Recent Activity</h3>

                        <p>Latest GRNs and deliveries</p>
                    </div>
                </div>

                {activity.length === 0 ? (
                    <div className="empty-state">
                        No activity available.
                    </div>
                ) : (
                    <div className="table-wrapper">
                        <table>
                            <thead>
                                <tr>
                                    <th>Type</th>
                                    <th>Document</th>
                                    <th>Customer</th>
                                    <th>Vehicle</th>
                                    <th>Date</th>
                                </tr>
                            </thead>

                            <tbody>
                                {activity.map((item, index) => (
                                    <tr
                                        key={`${item.type ?? "activity"}-${
                                            item.id ?? index
                                        }-${index}`}
                                    >
                                        <td>
                                            <span
                                                className={
                                                    item.type === "GRN"
                                                        ? "badge green"
                                                        : "badge orange"
                                                }
                                            >
                                                {item.type || "-"}
                                            </span>
                                        </td>

                                        <td>{item.documentNo || "-"}</td>

                                        <td>{item.customerName || "-"}</td>

                                        <td>{item.vehicleNo || "-"}</td>

                                        <td>
                                            {item.date
                                                ? new Date(
                                                      item.date
                                                  ).toLocaleDateString("en-IN")
                                                : "-"}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Non-blocking refresh error: existing data stays visible. */}
            {error && data && (
                <div className="page-error" role="status">
                    <p>
                        Could not refresh the latest dashboard data. Your
                        currently displayed data is still available.
                    </p>

                    <button
                        type="button"
                        onClick={() => loadDashboard(true)}
                    >
                        <RefreshCw size={16} />
                        Retry refresh
                    </button>
                </div>
            )}
        </div>
    );
};

export default Dashboard;
